/**
 * TalariCloud API client.
 *
 * One place that knows about tokens: it refreshes on a 401 and retries the
 * original request once, so nothing above this file has to think about it.
 */

import type {
  Project,
  DeploymentPlan,
  ProjectRelease,
  RepoWebhook,
  Activity,
  AdminOverview,
  OperatorAnalytics,
  Infrastructure,
  AdminUserDetail,
  AdminDatabaseRow,
  AdminDeployRow,
  AdminServiceRow,
  AdminUserRow,
  PlanQuote,
  AuditEntry,
  BetaRequest,
  BetaRequestStatus,
  BlueprintApplied,
  DatabaseBackup,
  DatabaseCredentials,
  DatabaseEngine,
  DatabaseLink,
  DatabasePlan,
  Deployment,
  Detection,
  Domain,
  EnvVar,
  LogLine,
  LoginResponse,
  MailAlias,
  Mailbox,
  MailboxCreated,
  MailboxCredentials,
  MailDomain,
  MailPricing,
  MailPlan,
  ManagedDatabase,
  Plan,
  PlanTier,
  Repository,
  Service,
  ServiceDetail,
  ServiceMetrics,
  SuspensionResult,
  TokenPair,
  Transaction,
  TwoFactorEnabled,
  TwoFactorSetup,
  User,
  Wallet,
  WalletAdjustResult,
  Statement,
} from './types'

/**
 * Where the dashboard talks to the control plane.
 *
 * Vite inlines this at build time, so a production image built without
 * `VITE_API_URL` bakes in the development fallback and ships it. That failure
 * is invisible until someone signs in: the page loads, the bundle is correct,
 * and every request — including the GitHub redirect — goes to the visitor's
 * own machine. It reads as a broken login rather than a missing build
 * argument.
 *
 * So the fallback applies to development only, and a production build without
 * it fails here instead.
 */
function resolveApiUrl(): string {
  // Production dashboard and operator-console traffic stays on the origin that
  // served the page. Traefik routes /v1 directly to the control plane. This
  // removes the extra browser hop to api.talaricloud.com (and therefore CORS,
  // separate edge routing and a second TLS request) from every dashboard call.
  if (import.meta.env.PROD && typeof window !== 'undefined') {
    const host = window.location.hostname
    if (host === 'app.talaricloud.com' || host === 'admin.talaricloud.com') {
      return window.location.origin
    }
  }

  const configured = import.meta.env.VITE_API_URL as string | undefined
  if (configured) return configured.replace(/\/$/, '')

  if (import.meta.env.PROD) {
    throw new Error(
      'VITE_API_URL was not set when this bundle was built. The dashboard has ' +
        'no control plane to talk to. Rebuild with --build-arg VITE_API_URL=https://api.<domain>',
    )
  }
  return 'http://localhost:8000'
}

export const API_URL: string = resolveApiUrl()

// OAuth is intentionally pinned to the canonical API origin rather than the
// same-origin /v1 proxy used for normal dashboard traffic. The OAuth callback
// is registered on settings.api_url, and its HttpOnly browser-binding cookie
// is host-scoped. Starting OAuth on app.talaricloud.com and returning to
// api.talaricloud.com makes that cookie disappear and falsely reports
// "the sign-in attempt expired". Keep the handshake on one origin; normal API
// calls still stay same-origin for reliability.
const configuredApiUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '')
export const OAUTH_API_URL: string = configuredApiUrl || API_URL

const ACCESS_KEY = 'talaricloud.access'
const REFRESH_KEY = 'talaricloud.refresh'
const REFRESH_ATTEMPT_KEY = 'talaricloud.refresh-attempt'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const tokens = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  set(pair: TokenPair) {
    localStorage.setItem(ACCESS_KEY, pair.access_token)
    localStorage.setItem(REFRESH_KEY, pair.refresh_token)
    localStorage.removeItem(REFRESH_ATTEMPT_KEY)
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(REFRESH_ATTEMPT_KEY)
  },
}

/**
 * The customer an operator is currently acting for, if any.
 *
 * An operator opens a support session and from then on every request carries
 * `X-Support-Session`, which makes the control plane run it as that customer.
 * That is what makes the operator console able to do everything a customer
 * can do: it is not a parallel set of operator screens with a subset of the
 * buttons — it is the customer's own pages, on the customer's own endpoints,
 * which cannot drift out of step with what the customer has.
 *
 * Kept in `sessionStorage`: it belongs to this tab and this visit, and must
 * not outlive the browser being closed. Sessions expire server-side in thirty
 * minutes regardless.
 */
const ACTING_KEY = 'talaricloud.acting'

export type ActingAs = {
  id: string
  customer_id: string
  customer_email: string
  expires_at: string
}

const listeners = new Set<() => void>()

export const acting = {
  current(): ActingAs | null {
    try {
      const raw = sessionStorage.getItem(ACTING_KEY)
      return raw ? (JSON.parse(raw) as ActingAs) : null
    } catch {
      return null
    }
  },
  set(session: ActingAs | null) {
    try {
      if (session) sessionStorage.setItem(ACTING_KEY, JSON.stringify(session))
      else sessionStorage.removeItem(ACTING_KEY)
    } catch {
      // A browser with storage blocked still works; the session just does
      // not survive a reload, which is the safe direction.
    }
    listeners.forEach((fn) => fn())
  },
  subscribe(fn: () => void) {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },
}

async function parseError(response: Response): Promise<string> {
  try {
    const body = await response.json()
    if (typeof body.detail === 'string') return body.detail
    // FastAPI validation errors arrive as a list of field problems.
    if (Array.isArray(body.detail)) {
      return body.detail
        .map((item: { loc?: string[]; msg?: string }) =>
          item.loc ? `${item.loc.slice(1).join('.')}: ${item.msg}` : item.msg,
        )
        .join('; ')
    }
  } catch {
    /* fall through to the status text */
  }
  return response.statusText || 'request failed'
}

let refreshInFlight: Promise<boolean> | null = null

/**
 * Swap the refresh token for a fresh pair.
 *
 * Exported because the live event stream needs it too: `EventSource` cannot
 * retry a request with a new header, so a reconnect refreshes first.
 */
export async function refreshSession(): Promise<boolean> {
  return refreshTokens()
}

async function refreshTokens(expectedAccess = tokens.access()): Promise<boolean> {
  if (!refreshInFlight) {
    const rotate = async () => {
      // Another request/tab may have already rotated while we waited.
      if (tokens.access() && tokens.access() !== expectedAccess) return true
      const refresh = tokens.refresh()
      if (!refresh) return false
      let attempt: { token: string; id: string } | null = null
      try { attempt = JSON.parse(localStorage.getItem(REFRESH_ATTEMPT_KEY) || 'null') } catch { /* replace invalid state */ }
      if (attempt?.token !== refresh || !attempt.id) {
        attempt = { token: refresh, id: crypto.randomUUID().replaceAll('-', '') }
        localStorage.setItem(REFRESH_ATTEMPT_KEY, JSON.stringify(attempt))
      }
      let response: Response
      try {
        response = await fetch(`${API_URL}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Refresh-Attempt': attempt.id },
          body: JSON.stringify({ refresh_token: refresh }),
        })
      } catch {
        throw new ApiError('Connection interrupted. Your session is saved; retry when connected.', 503)
      }
      // Never erase a newer login or restore a session after sign-out.
      if (tokens.refresh() !== refresh) return Boolean(tokens.access())
      if (response.status === 401) {
        tokens.clear()
        return false
      }
      if (!response.ok) throw new ApiError('Session renewal is temporarily unavailable. Please retry.', 503)
      const pair = (await response.json()) as TokenPair
      if (tokens.refresh() !== refresh) return Boolean(tokens.access())
      tokens.set(pair)
      return true
    }
    // Refresh tokens are single-use across every tab, not just this module.
    const operation = navigator.locks
      ? navigator.locks.request('talaricloud.session.refresh', rotate)
      : rotate()
    refreshInFlight = Promise.resolve(operation).finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

export async function request<T>(
  path: string,
  options: RequestInit = {},
  retry = true,
  baseUrl = API_URL,
): Promise<T> {
  const headers = new Headers(options.headers)
  headers.set('Accept', 'application/json')
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  const access = tokens.access()
  if (access) headers.set('Authorization', `Bearer ${access}`)
  // Sent on every request, including the console's own admin pages: the
  // control plane ignores it outside a customer resource and answers as the
  // operator, so the client never has to know which paths are which.
  const supporting = acting.current()
  if (supporting && !headers.has('X-Support-Session')) {
    headers.set('X-Support-Session', supporting.id)
  }

  let response: Response
  try {
    response = await fetch(`${baseUrl}${path}`, { credentials: 'include', ...options, headers })
  } catch {
    throw new ApiError(
      'TalariCloud API is temporarily unreachable. Your session and deployment state are preserved; retry shortly.',
      503,
    )
  }

  if (response.status === 401 && retry && tokens.refresh()) {
    if (await refreshTokens(access)) return request<T>(path, options, false, baseUrl)
  }

  if (!response.ok) throw new ApiError(await parseError(response), response.status)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/**
 * Fetch a file rather than JSON, and hand it to the browser to save.
 *
 * Separate from `request` because that one parses every response as JSON — a
 * database dump is bytes, and reading it as text would corrupt it.
 */
export async function fetchBlob(path: string, retry = true): Promise<Blob> {
  const headers = new Headers()
  const access = tokens.access()
  if (access) headers.set('Authorization', `Bearer ${access}`)
  const supporting = acting.current()
  if (supporting) headers.set('X-Support-Session', supporting.id)

  let response: Response
  try {
    response = await fetch(`${API_URL}${path}`, { headers })
  } catch {
    throw new ApiError(
      'TalariCloud API is temporarily unreachable. Retry shortly.',
      503,
    )
  }

  if (response.status === 401 && retry && tokens.refresh()) {
    if (await refreshTokens(access)) return fetchBlob(path, false)
  }
  if (!response.ok) throw new ApiError(await parseError(response), response.status)

  return response.blob()
}

export async function download(path: string, filename: string): Promise<void> {
  const url = URL.createObjectURL(await fetchBlob(path))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const get = <T>(path: string) => request<T>(path)
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined })
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) })
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' })

export const api = {
  projects: () => get<Project[]>('/v1/projects'),
  project: (id: string) => get<Project>(`/v1/projects/${id}`),
  /** What moving the whole application to `plan` costs right now. */
  projectPlanQuote: (id: string, plan: string) =>
    get<PlanQuote>(`/v1/projects/${id}/plan-quote?plan=${encodeURIComponent(plan)}`),
  /** Move the application — every compute component — to `plan`. */
  paidPlanQuote: (id: string, plan: string) => get<PlanQuote>(`/v1/projects/${id}/paid-plan/quote?plan=${encodeURIComponent(plan)}`),
  buyPaidPlan: (id: string, plan: string, maximum_charge: string) => put<Project>(`/v1/projects/${id}/paid-plan`, { plan, maximum_charge }),
  cancelPaidPlan: (id: string) => del<Project>(`/v1/projects/${id}/paid-plan`),
  changeProjectPlan: (id: string, plan: string, expandStorage = false) => patch<Project>(`/v1/projects/${id}`, { plan, expand_storage: expandStorage }),
  renameProject: (id: string, name: string) => patch<Project>(`/v1/projects/${id}`, { name }),
  projectPlan: (id: string) => get<DeploymentPlan>(`/v1/projects/${id}/plan`),
  deployProject: (id: string, reviewToken?: string) => post<ProjectRelease>(`/v1/projects/${id}/deploys`, reviewToken ? { review_token: reviewToken } : {}),
  cancelProjectRelease: (id: string, releaseId: string) => post<ProjectRelease>(`/v1/projects/${id}/deploys/${releaseId}/cancel`, {}),
  retryProjectRelease: (id: string, releaseId: string) => post<ProjectRelease>(`/v1/projects/${id}/deploys/${releaseId}/retry`, {}),
  // --- auth ---
  signup: (email: string, password: string, name?: string, invite_token?: string) =>
    post<TokenPair>('/v1/auth/signup', { email, password, name, invite_token }),
  /** Tokens, or — for an account with a second factor — a challenge to answer
   *  with `mfa`. */
  login: (email: string, password: string) =>
    post<LoginResponse>('/v1/auth/login', { email, password }),
  mfa: (mfaToken: string, code: string) =>
    post<TokenPair>('/v1/auth/mfa', { mfa_token: mfaToken, code }),
  me: () => get<User>('/v1/auth/me'),
  /** Retires this device's refresh token. Takes the token rather than the
   *  header so it works from a tab whose access token expired long ago. */
  logout: (refreshToken: string) =>
    post<{ detail: string }>('/v1/auth/logout', { refresh_token: refreshToken }),
  /** Signs out every other device; the returned pair keeps this one in. */
  logoutAll: () => post<TokenPair>('/v1/auth/logout-all'),
  /** A single-use, sixty-second ticket for the event stream or a log socket —
   *  what goes in the URL instead of the access token, because URLs are
   *  written to access logs. */
  streamTicket: () => post<{ ticket: string; expires_in: number }>('/v1/auth/ticket'),
  features: () => get<{ github: boolean; invite_only: boolean }>('/v1/auth/features'),
  requestInvite: (body: { email: string; title: string | null; message: string | null; website: string }) =>
    post<{ detail: string }>('/v1/beta-requests', body),
  // Set the browser-binding cookie on the same host as GitHub's callback.
  connectGithub: () => request<{ url: string }>(
    '/v1/auth/github/connect', { method: 'POST' }, true, OAUTH_API_URL,
  ),
  githubStartUrl: () => `${OAUTH_API_URL}/v1/auth/github/start`,
  disconnectGithub: () => post<User>('/v1/auth/github/disconnect'),

  // --- github ---
  repos: (search = '', page = 1) =>
    get<Repository[]>(`/v1/github/repos?page=${page}&search=${encodeURIComponent(search)}`),
  branches: (repoFullName: string) =>
    get<string[]>(`/v1/github/repos/${repoFullName}/branches`),
  detect: (repoFullName: string, branch?: string, root?: string) => {
    const query = new URLSearchParams()
    if (branch) query.set('branch', branch)
    if (root) query.set('root', root)
    const suffix = query.toString()
    return get<Detection>(
      `/v1/github/repos/${repoFullName}/detect${suffix ? `?${suffix}` : ''}`,
    )
  },

  // --- blueprints ---
  applyBlueprint: (body: Record<string, unknown>) =>
    post<BlueprintApplied>('/v1/blueprints/apply', body),

  // --- services ---
  services: () => get<Service[]>('/v1/services'),
  service: (id: string) => get<ServiceDetail>(`/v1/services/${id}`),
  createService: (body: Record<string, unknown>) =>
    post<ServiceDetail>('/v1/services', body),
  updateService: (id: string, body: Record<string, unknown>) =>
    patch<ServiceDetail>(`/v1/services/${id}`, body),
  deleteService: (id: string) => del<{ detail: string }>(`/v1/services/${id}`),
  deployApplication: (id: string) => post<ProjectRelease>(`/v1/services/${id}/deploy-application`, {}),
  /** Ask GitHub for the push webhook again, right now, and return what it said. */
  repairWebhook: (id: string) => post<RepoWebhook>(`/v1/services/${id}/webhook`, {}),
  deploy: (id: string, commitSha?: string) =>
    post<Deployment>(`/v1/services/${id}/deploys`, { commit_sha: commitSha ?? null }),
  deploys: (id: string) => get<Deployment[]>(`/v1/services/${id}/deploys`),
  suspend: (id: string) => post<{ detail: string }>(`/v1/services/${id}/suspend`),
  resume: (id: string) => post<{ detail: string }>(`/v1/services/${id}/resume`),
  restart: (id: string) => post<{ detail: string }>(`/v1/services/${id}/restart`),
  runtimeLogs: (id: string, tail = 200) =>
    get<string[]>(`/v1/services/${id}/logs?tail=${tail}`),

  serviceMetrics: (id: string, windowMinutes = 60) =>
    get<ServiceMetrics>(`/v1/services/${id}/metrics?window_minutes=${windowMinutes}`),

  // --- env & domains ---
  envVars: (id: string) => get<EnvVar[]>(`/v1/services/${id}/env`),
  setEnvVars: (
    id: string,
    // `value: null` keeps whatever is stored — the only way to describe an
    // environment that contains secrets this page has never been shown.
    variables: {
      key: string
      value: string | null
      is_secret: boolean
      build_time?: boolean
    }[],
    replace = false,
  ) => put<EnvVar[]>(`/v1/services/${id}/env`, { variables, replace }),
  deleteEnvVar: (id: string, key: string) =>
    del<{ detail: string }>(`/v1/services/${id}/env/${encodeURIComponent(key)}`),
  revealEnvVar: (id: string, key: string) =>
    get<{ key: string; value: string }>(
      `/v1/services/${id}/env/${encodeURIComponent(key)}/reveal`,
    ),
  domains: (id: string) => get<Domain[]>(`/v1/services/${id}/domains`),
  addDomain: (id: string, hostname: string) =>
    post<Domain>(`/v1/services/${id}/domains`, { hostname }),
  checkDomain: (serviceId: string, domainId: string) =>
    post<import('./types').DomainCheck>(`/v1/services/${serviceId}/domains/${domainId}/check`, {}),
  verifyDomain: (id: string, domainId: string) =>
    post<Domain>(`/v1/services/${id}/domains/${domainId}/verify`),
  removeDomain: (id: string, domainId: string) =>
    del<{ detail: string }>(`/v1/services/${id}/domains/${domainId}`),

  // --- deploys ---
  activity: (limit = 20) => get<Activity[]>(`/v1/deploys?limit=${limit}`),
  deployment: (id: string) => get<Deployment>(`/v1/deploys/${id}`),
  deploymentLogs: (id: string, after = 0) =>
    get<LogLine[]>(`/v1/deploys/${id}/logs?after=${after}`),
  cancelDeploy: (id: string) => post<{ detail: string }>(`/v1/deploys/${id}/cancel`),
  rollback: (id: string) => post<Deployment>(`/v1/deploys/${id}/rollback`),

  // --- managed databases ---
  databases: () => get<ManagedDatabase[]>('/v1/databases'),
  database: (id: string) => get<ManagedDatabase>(`/v1/databases/${id}`),
  databasePlans: () => get<DatabasePlan[]>('/v1/databases/plans'),
  databaseEngines: () => get<{
    engine: DatabaseEngine
    default_version: string
    versions: string[]
  }[]>('/v1/databases/engines'),
  createDatabase: (body: {
    name: string
    engine: DatabaseEngine
    plan: PlanTier
    version?: string
    disk_size_gb?: number
  }) => post<ManagedDatabase>('/v1/databases', body),
  databaseCredentials: (id: string) =>
    get<DatabaseCredentials>(`/v1/databases/${id}/connection`),
  deleteDatabase: (id: string, deleteData = false) =>
    del<{ detail: string }>(`/v1/databases/${id}?delete_data=${deleteData}`),

  // --- backups ---
  backups: (databaseId: string) =>
    get<DatabaseBackup[]>(`/v1/databases/${databaseId}/backups`),
  createBackup: (databaseId: string) =>
    post<DatabaseBackup>(`/v1/databases/${databaseId}/backups`),
  downloadBackup: (databaseId: string, backupId: string, filename: string) =>
    download(
      `/v1/databases/${databaseId}/backups/${backupId}/download`,
      filename,
    ),
  /** Returns the safety copy taken first, not the backup that was restored. */
  restoreBackup: (databaseId: string, backupId: string) =>
    post<DatabaseBackup>(
      `/v1/databases/${databaseId}/backups/${backupId}/restore`,
    ),

  // --- database links ---
  serviceDatabases: (serviceId: string) =>
    get<DatabaseLink[]>(`/v1/services/${serviceId}/databases`),
  linkDatabase: (serviceId: string, databaseId: string, envVarName?: string) =>
    post<DatabaseLink>(`/v1/services/${serviceId}/databases`, {
      database_id: databaseId,
      env_var_name: envVarName ?? null,
    }),
  unlinkDatabase: (serviceId: string, linkId: string) =>
    del<{ detail: string }>(`/v1/services/${serviceId}/databases/${linkId}`),

  // --- Talari Mail ---
  // Domains, payment and mailbox administration only. Reading and sending
  // happen in Woyra Mail, reached through `openMailbox` or with the address
  // and password a mailbox is given at creation.
  mailDomains: () => get<MailDomain[]>('/v1/mail/domains'),
  addMailDomain: (name: string) => post<MailDomain>('/v1/mail/domains', { name }),
  checkMailDomain: (domainId: string) =>
    post<MailDomain>(`/v1/mail/domains/${encodeURIComponent(domainId)}/check`, {}),
  removeMailDomain: (domainId: string) =>
    del<{ detail: string }>(`/v1/mail/domains/${encodeURIComponent(domainId)}`),
  /** Mail plan prices, set on the server so Sales can change them
   *  without a dashboard release. */
  mailPricing: () => get<MailPricing>('/v1/mail/pricing'),
  mailboxes: (domainId: string) =>
    get<Mailbox[]>(`/v1/mail/domains/${encodeURIComponent(domainId)}/mailboxes`),
  /** The first month is charged before the mailbox exists; a 402 names the
   *  shortfall. The credentials in the reply are shown once and not kept. */
  createMailbox: (
    domainId: string,
    body: { local_part: string; plan?: MailPlan; display_name?: string | null; quota_bytes?: number | null },
  ) => post<MailboxCreated>(`/v1/mail/domains/${encodeURIComponent(domainId)}/mailboxes`, body),
  updateMailbox: (
    mailboxId: string,
    body: { display_name?: string | null; quota_bytes?: number | null; suspended?: boolean },
  ) => patch<Mailbox>(`/v1/mail/mailboxes/${encodeURIComponent(mailboxId)}`, body),
  deleteMailbox: (mailboxId: string) =>
    del<{ detail: string }>(`/v1/mail/mailboxes/${encodeURIComponent(mailboxId)}`),
  /** An hour-long signed-in link into Woyra Mail. It is a credential: open it
   *  and do not show or keep it. */
  openMailbox: (mailboxId: string) =>
    post<{ url: string }>(`/v1/mail/mailboxes/${encodeURIComponent(mailboxId)}/open`, {}),
  /** A fresh password, generated and set at the provider. Every session the
   *  mailbox had is ended, and the reply is the only time it is readable. */
  resetMailboxPassword: (mailboxId: string) =>
    post<MailboxCredentials>(`/v1/mail/mailboxes/${encodeURIComponent(mailboxId)}/password`, {}),
  mailAliases: (domainId: string) =>
    get<MailAlias[]>(`/v1/mail/domains/${encodeURIComponent(domainId)}/aliases`),
  /** Create or repoint: PUT, because sales@ outliving the person answering
   *  it is the whole point of an alias. */
  setMailAlias: (domainId: string, localPart: string, destinations: string[]) =>
    put<MailAlias>(`/v1/mail/domains/${encodeURIComponent(domainId)}/aliases`, {
      local_part: localPart,
      destinations,
    }),
  deleteMailAlias: (domainId: string, localPart: string) =>
    del<{ detail: string }>(
      `/v1/mail/domains/${encodeURIComponent(domainId)}/aliases/${encodeURIComponent(localPart)}`,
    ),

  // --- account ---
  updateAccount: (body: { name?: string; phone?: string }) =>
    patch<User>('/v1/account', body),
  /** Every other session is signed out; the returned pair keeps this one in. */
  changePassword: (currentPassword: string | null, newPassword: string) =>
    post<TokenPair>('/v1/account/password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  /** Two-factor: setup hands back a secret to scan; enable confirms it with
   *  the first code and returns recovery codes (shown once) plus a fresh pair,
   *  because enabling signs every other session out. */
  twoFactorSetup: (currentPassword: string | null) =>
    post<TwoFactorSetup>('/v1/account/2fa/setup', { current_password: currentPassword }),
  twoFactorEnable: (code: string) =>
    post<TwoFactorEnabled>('/v1/account/2fa/enable', { code }),
  twoFactorDisable: (code: string, currentPassword: string | null) =>
    post<{ detail: string }>('/v1/account/2fa/disable', {
      code,
      current_password: currentPassword,
    }),
  exportAccount: () => get<Record<string, unknown>>('/v1/account/export'),
  deleteAccount: (confirmEmail: string) =>
    request<{ detail: string }>('/v1/account', {
      method: 'DELETE',
      body: JSON.stringify({ confirm_email: confirmEmail }),
    }),

  // --- billing ---
  billingCurrency: (currency: string) => put<Wallet>('/v1/billing/wallet/currency', { currency }),
  wallet: () => get<Wallet>('/v1/billing/wallet'),
  statement: () => get<Statement>('/v1/billing/statement'),
  transactions: () => get<Transaction[]>('/v1/billing/transactions'),
  plans: () => get<Plan[]>('/v1/billing/plans'),
  topUp: (amount: string, provider: string, phone?: string) =>
    post<{ checkout_url: string; reference: string }>('/v1/billing/topup', {
      amount,
      provider,
      phone,
    }),
  paymentStatus: (reference: string) =>
    get<{ status: string; balance: string; currency: string }>(`/v1/billing/payments/${encodeURIComponent(reference)}`),
  topUpQuote: (amount: string, provider: string) =>
    post<{ quote: string; amount: string; charge_amount: string; charge_currency: string; exp: number }>(
      '/v1/billing/international/quote', { amount, provider },
    ),
  internationalCheckout: (quote: string) =>
    post<{ checkout_url: string; reference: string }>('/v1/billing/international/checkout', { quote }),

  // --- operator console (every call 403s for a customer) ---
  servicePlanQuote: (id: string, plan: string) =>
    get<PlanQuote>(
      `/v1/services/${encodeURIComponent(id)}/plan-quote?plan=${encodeURIComponent(plan)}`,
    ),
  databasePlanQuote: (id: string, plan: string) =>
    get<PlanQuote>(
      `/v1/databases/${encodeURIComponent(id)}/plan-quote?plan=${encodeURIComponent(plan)}`,
    ),
  updateDatabase: (id: string, plan: string, expandStorage = false) =>
    patch<ManagedDatabase>(`/v1/databases/${encodeURIComponent(id)}`, { plan, expand_storage: expandStorage }),
  retryDatabase: (id: string) =>
    post<ManagedDatabase>(`/v1/databases/${encodeURIComponent(id)}/retry`),

  admin: {
    manageDatabase: (id: string, action: 'remove' | 'retry', body: {
      confirm_slug: string
      reason: string
      delete_data: boolean
      detach_services: boolean
    }) => post<{ detail: string }>(`/v1/admin/databases/${encodeURIComponent(id)}/${action}`, body),
    overview: () => get<AdminOverview>('/v1/admin/overview'),
    analytics: (days = 30) => get<OperatorAnalytics>(`/v1/admin/analytics?days=${days}`),
    infrastructure: () => get<Infrastructure>('/v1/admin/infrastructure'),
    services: (status?: string, q?: string) => {
      const query = new URLSearchParams()
      if (status) query.set('status', status)
      if (q) query.set('q', q)
      const suffix = query.toString()
      return get<AdminServiceRow[]>(`/v1/admin/services${suffix ? `?${suffix}` : ''}`)
    },
    databases: (status?: string, q?: string) => {
      const query = new URLSearchParams()
      if (status) query.set('status', status)
      if (q) query.set('q', q)
      const suffix = query.toString()
      return get<AdminDatabaseRow[]>(`/v1/admin/databases${suffix ? `?${suffix}` : ''}`)
    },
    deploys: (status?: string) =>
      get<AdminDeployRow[]>(`/v1/admin/deploys${status ? `?status=${status}` : ''}`),
    users: (q?: string) =>
      get<AdminUserRow[]>(`/v1/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    user: (id: string) => get<AdminUserDetail>(`/v1/admin/users/${encodeURIComponent(id)}`),
    adjustWallet: (id: string, amount: string, reason: string, reference: string, currency = 'ETB') =>
      post<WalletAdjustResult>(`/v1/admin/users/${id}/wallet`, { amount, reason, reference, currency }),
    suspend: (id: string, reason: string) =>
      post<SuspensionResult>(`/v1/admin/users/${id}/suspend`, { reason }),
    restore: (id: string) => post<SuspensionResult>(`/v1/admin/users/${id}/restore`),
    setRole: (id: string, isAdmin: boolean) =>
      post<AdminUserRow>(`/v1/admin/users/${id}/role`, { is_admin: isAdmin }),
    betaRequests: (status?: BetaRequestStatus, held = false) => {
      // `held` reaches the requests that tripped the honeypot. Mostly bots,
      // and not only bots — the trap is a field browser autofill can reach.
      const query = new URLSearchParams()
      if (status) query.set('status', status)
      if (held) query.set('held', 'true')
      const suffix = query.toString()
      return get<BetaRequest[]>(`/v1/admin/beta-requests${suffix ? `?${suffix}` : ''}`)
    },
    updateBetaRequest: (id: string, body: { status?: BetaRequestStatus; note?: string }) =>
      post<BetaRequest>(`/v1/admin/beta-requests/${id}`, body),
    // Somebody met at a meetup or introduced, who never used the website form.
    addBetaRequest: (body: {
      email: string
      title?: string
      message?: string
      invite?: boolean
    }) => post<BetaRequest>('/v1/admin/beta-requests', body),
    audit: (filters: { action?: string; actor?: string; q?: string; offset?: number }) => {
      const query = new URLSearchParams()
      if (filters.action) query.set('action', filters.action)
      if (filters.actor) query.set('actor', filters.actor)
      if (filters.q) query.set('q', filters.q)
      if (filters.offset) query.set('offset', String(filters.offset))
      const suffix = query.toString()
      return get<AuditEntry[]>(`/v1/admin/audit${suffix ? `?${suffix}` : ''}`)
    },
  },
}

/** WebSocket URL for a deployment's live build log.
 *
 * Asynchronous because it first fetches a single-use ticket: the URL is what
 * every proxy writes to its access log, and a ticket there is worthless a
 * minute later where an access token would be an hour of the session. */
export async function logSocketUrl(deploymentId: string): Promise<string> {
  const { ticket } = await api.streamTicket()
  const base = API_URL.replace(/^http/, 'ws')
  return `${base}/v1/deploys/${deploymentId}/stream?ticket=${encodeURIComponent(ticket)}`
}



